package util

import common.dto.ElementType
import editor.EditorManager
import editor.capture.CaptureBackground
import ext.rect
import kotlinx.browser.window
import kotlinx.coroutines.await
import pdflib.*
import kotlin.math.ceil
import kotlin.math.floor
import kotlin.math.sqrt

object PdfManager {
    /**
     * @param secondPageBackground should the second page have a background or just white? For outline mode this
     *                             looks better.
     */
    suspend fun createPdf(images: List<String>, background: CaptureBackground, secondPageBackground: Boolean) {
        val document = PDFDocument.create("anleitung.pdf")
        val pdfDoc = document.await()
        pdfDoc.contentPage(background)
        pdfDoc.imagePage(images, background.takeIf { secondPageBackground })

        val pdfBytes = pdfDoc.save().await()
        // todo let user set name of structure
        DownloadHelper.triggerDownload(pdfBytes, fileName = "anleitung.pdf", fileType = "application/pdf")
    }

    private suspend fun PDFDocument.contentPage(background: CaptureBackground) = addPage().apply {
        // background
        rect(x = 0, y = 0, width = getSize().width, height = getSize().height, color = background.color)

        val size = getSize()

        val structure = EditorManager.getStructure()

        val padding = ((getSize().width - mmToPt(32 * 5)) / 2).toInt()

        // brixi
        val brixiSizePt = mmToPt(32)
        val brixiCount = structure.count { it.type == ElementType.Brixi }
        renderPngNormal("icons/brixi-filled-thin-big.png", this, x = padding, y = size.height - (padding / 1.5) - brixiSizePt, width = brixiSizePt, opacity = if (brixiCount >= 1) 1 else 0.5)
        if (brixiCount >= 1) drawCountNextTo(padding, (size.height - (padding / 1.5) - brixiSizePt + mmToPt(16) - 8).toInt(), brixiCount)

        // twister
        val twisterSizePt = mmToPt(8.65*2)
        val twisterCount = structure.count { it.type == ElementType.Twister }
        val twisterOffset = (brixiSizePt - twisterSizePt) / 2
        renderPngNormal("icons/twister-thin.png", this, x = padding + brixiSizePt + (padding)/*size.width - padding - twisterSizePt*/, y = size.height - (padding / 1.5) - twisterSizePt - twisterOffset, width = twisterSizePt, opacity = if (twisterCount >= 1) 1 else 0.5)
        if (twisterCount >= 1) drawCountNextTo(padding + brixiSizePt.toInt() + (padding).toInt(), (size.height - (padding / 1.5) - brixiSizePt + mmToPt(
            16
        ) - 8).toInt(), twisterCount)

        @Suppress("UnnecessaryVariable", "RedundantSuppression")
        val foursStartY = padding
        drawElements(startX = padding, startY = foursStartY, offset = 24 + 12,
            ElementType.`4x3`
        )

        val onesStartY = foursStartY + mmToPt(32).toInt() + (padding / 1.5).toInt()
        drawElements(startX = padding, startY = onesStartY, offset = 8 + 8,
            ElementType.`1x2`, ElementType.`1x3`, ElementType.`1x4`, ElementType.`1x5`
        )

        val twosStartY = onesStartY + mmToPt(16 * 3).toInt() + mmToPt(8).toInt() + (padding / 1.5).toInt()
        drawElements(startX = padding, startY = twosStartY, offset = /*16 + 12*/16 + 8,
            ElementType.`2x2`, ElementType.`2x3`, ElementType.`2x4`, ElementType.`2x5`
        )
    }

    private fun PDFPage.drawElements(startX: Int, startY: Int, offset: Int, vararg elements: ElementType) = elements.forEachIndexed { index, elt ->
        val count = EditorManager.getStructure().count { it.type == elt }
        drawRectangle(object : PDFPageDrawRectangleOptions {
            override var x: Number? = startX
            override var y: Number? = startY + index * mmToPt(offset)
            override var width: Number? = mmToPt(32) * elt.lengthMultiplier
            override var height: Number? = mmToPt(8) * elt.heightMultiplier
            override var color: RGB? = rgb(1.0, 1.0, 1.0)
            override var borderColor: RGB? = rgb(0.0, 0.0, 0.0)
            override var borderWidth: Number? = 1
            override var opacity: Number? = if (count >= 1) 1 else 0.5
            override var borderOpacity: Number? = if (count >= 1) 1 else 0.5
        })
        if (count >= 1) drawCountNextTo(startX, (startY + index * mmToPt(offset) + mmToPt(4) * elt.heightMultiplier - 8).toInt(), count)
    }

    private fun PDFPage.drawCountNextTo(x: Int, y: Int, count: Int) {
        drawText(count.toString(), object : TextDrawingOptions {
            override var x: Number? = x - 30
            override var y: Number? = y
            override var size: Number? = 25
            override var color: RGB? = rgb(1.0, 1.0, 1.0)
        })
    }

    private fun mmToPt(mm: Number) = mm.toDouble() * 2.83465

    private suspend fun PDFDocument.imagePage(images: List<String>, background: CaptureBackground?) = addPage().apply {
        background?.let {
            rect(x = 0, y = 0, width = getSize().width, height = getSize().height, color = it.color)
        }

        val size = getSize()

        val pngs = images.map { embedPng(it).await() }

        pngs.forEachIndexed { index, png ->
            val resultingWidth = (png.width.toDouble() / png.height.toDouble()) * (size.height / 2)
            val offset = (resultingWidth - size.width) / 2
            drawImage(png, object : ImageDrawingOptions {
                override var x: Number? = -offset
                override var y: Number? = index * size.height / 2
                override var height: Number? = size.height / 2
                override var width: Number? = resultingWidth
            })
        }
    }

    private suspend fun PDFDocument.renderPngNormal(path: String, page: PDFPage, x: Number, y: Number, width: Number, opacity: Number = 1) {
        val data = window.fetch(path).await().arrayBuffer().await()
        val png = embedPng(data).await()
        page.drawImage(png, object : ImageDrawingOptions {
            override var x: Number? = x
            override var y: Number? = y
            override var width: Number? = width
            override var height: Number? = width.toDouble() * (png.height.toDouble() / png.width.toDouble())
            override var opacity: Number? = opacity
        })
    }

    private fun calculatePosition(index: Int, total: Int, size: Size): Pair<Double, Double> {
        val rows = ceil(sqrt(total.toDouble()))
        val cols = ceil(total.toDouble() / rows)
        val row = floor(index.toDouble() / cols)
        val col = index % cols
        return (col * size.width / cols) to (row * size.height / rows)
    }
}